import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import queryClient from 'react/services/queryClient';
import { renderComponent } from 'react/utils/renderUtils';
import RouteDataModal from 'react/containers/modals/RouteOtherData';

const otherDataCallback = (routeModel) => {
  renderComponent(
    <RouteDataModal open="true" route={routeModel} i18n={Monet.I18n} />,
    document.getElementById('templates_container')
  );
};
/**
 * Custom hook that provides actions for routes
 * Following Single Responsibility and Interface Segregation principles
 *
 * @param {Object} route - The route object
 * @returns {Object} - Various route action functions
 */
export const useRouteActions = (route) => {
  const dispatch = useDispatch();

  const navigateToRouteDetails = useCallback(() => {
    dispatch(push(`/maps/route/${route.id}/details`));
  }, [dispatch, route.id]);

  const navigateToRouteSummary = useCallback(() => {
    const path = `/RouteSummary?routeKey=${route.key}&date=${route.date}`;
    dispatch(push(path));
    App.Views.RouteSummary.showGridRoutes();
  }, [dispatch, route.key, route.date]);

  const navigateToRouteEdit = useCallback(() => {
    dispatch(push(`/Route/edit/${route.id}`));
  }, [dispatch, route.id]);

  const startRoute = useCallback(() => {
    const startRouteObj = new Greenmile.Route.Start({
      parentSelector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    });

    startRouteObj.setRoute(new App.Models.Route(route));
    startRouteObj.show();
  }, [route]);

  const completeRoute = useCallback(() => {
    const completeRouteObj = new Greenmile.Route.Complete({
      parentSelector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    });

    completeRouteObj.setRoute(new App.Models.Route(route));
    completeRouteObj.show();
  }, [route]);

  const departOrigin = useCallback(() => {
    const departOriginObj = new Greenmile.Route.DepartOrigin({
      parentSelector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    });

    departOriginObj.setRoute(new App.Models.Route(route));
    departOriginObj.show();
  }, [route]);

  const arriveDestination = useCallback(() => {
    const arriveDestinationObj = new Greenmile.Route.ArriveDestination({
      parentSelector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    });

    arriveDestinationObj.setRoute(new App.Models.Route(route));
    arriveDestinationObj.show();
  }, [route]);

  const cloneRoute = useCallback(() => {
    const completeRouteObj = new Greenmile.Route.Clone('RouteMonitor');
    completeRouteObj.setRoute(route);
    completeRouteObj.show();
  }, [route]);

  const focusOnMap = useCallback(
    (equipmentAssignments) => {
      if (!equipmentAssignments?.length) return;

      const view = new Greenmile.RouteMonitor.Routes();
      const primaryAssignment = equipmentAssignments.find(
        (item) => item.principal
      );

      if (primaryAssignment) {
        const routeCopy = { ...route };
        routeCopy.primaryEquipment = primaryAssignment.equipment;
        view.goToTruck(routeCopy);
      }
    },
    [route]
  );

  const transferStops = useCallback(() => {
    const transferStopsObj = new Greenmile.Route.TransferStops.Popup({
      parentSelector: '#RouteMonitor'
    });

    transferStopsObj.setFromRouteId(route.id);
    transferStopsObj.showTransferStops();
  }, [route.id]);

  const showOtherData = useCallback(() => {
    const routeModel = new App.Models.Route(route);

    routeModel.filterWithRestrictions({
      criteria:
        '{"filters":["additionalMaterials.transport.key","additionalMaterials.quantity","additionalMaterials.sku.key","additionalMaterials.sku.description","additionalMaterials.sku.organization.key","holderMaterials.id","holderMaterials.transport.id","holderMaterials.transport.key","holderMaterials.sku.key","holderMaterials.sku.description","holderMaterials.sku.organization.key","holderMaterials.size1Quantity","holderMaterials.size2Quantity","holderMaterials.size3Quantity", "helperAssignments.helper.id","helperAssignments.helper.key","helperAssignments.helper.name","helperAssignments.helper.login","helperAssignments.helper.phone1","helperAssignments.helper.phone2","driverPendingBalances.description","driverPendingBalances.amount","driverPendingBalances.quantity","locationPendingPayments.location.id","locationPendingPayments.location.key","locationPendingPayments.location.description","locationPendingPayments.location.addressLine1","locationPendingPayments.location.addressLine2","locationPendingPayments.location.organization.key","locationPendingPayments.documentKey","locationPendingPayments.saleDate","locationPendingPayments.dueDate","locationPendingPayments.invoice","locationPendingPayments.amount","locationPendingPayments.amountPayed","locationPendingPayments.remark","extraLineItems.sku.description","extraLineItems.plannedSize1","extraLineItems.plannedSize2","extraLineItems.plannedSize3","extraLineItems.availableSize1","extraLineItems.availableSize2","extraLineItems.availableSize3"]}',
      success: function successCallback() {
        otherDataCallback(routeModel);
      }
    });
  }, [route]);

  return {
    navigateToRouteDetails,
    navigateToRouteSummary,
    navigateToRouteEdit,
    startRoute,
    completeRoute,
    departOrigin,
    arriveDestination,
    cloneRoute,
    focusOnMap,
    transferStops,
    showOtherData
  };
};

export default useRouteActions;
